// What a photo, video or recording says about itself, written out the way the people who read such
// things read them, and gathered into the groups the details sheet shows.
//
// The API hands the tags over in the file's own units (seconds of exposure, millimetres, degrees,
// bits a second, hertz), so every choice about how they read is made here. Each kind leads with the
// handful of facts its people look for first: an exposure in photographer's shorthand —
// ƒ/1.78 · 1/1600 s · ISO 80 · 24 mm — a video's resolution, frame rate, runtime and codec, a
// recording's runtime, sample rate, bit rate and channels. "describe" turns a file and its details
// into that lead and the groups that follow it; the formatters are exported for what else wants a
// fact written the same way.
//
// A moment is shown as the clock on the wall where it was taken said, not moved into the viewer's
// zone, since "4:12 PM at Big Sur" is the fact a photo records. Only a time a file kept in UTC, as
// videos do, is brought into the viewer's zone, there being no other zone to show it in.

import {fileExtension, formatBytes, formatTimestamp} from './fileTools'

const trimmed = (value, places) => String(Number(value.toFixed(places)))

export const formatAperture = fNumber => `ƒ/${trimmed(fNumber, 2)}`

// Fast shutters are written as the fraction a camera shows, slow ones in seconds.
export const formatShutter = seconds => {
  if (seconds >= 0.3) return `${trimmed(seconds, 1)} s`

  return `1/${Math.round(1 / seconds)} s`
}

export const formatFocalLength = millimetres => `${trimmed(millimetres, 1)} mm`

export const formatExposureBias = stops => {
  if (Math.abs(stops) < 0.05) return '0 EV'

  return `${stops > 0 ? '+' : '−'}${trimmed(Math.abs(stops), 1)} EV`
}

export const formatFrameRate = rate => `${trimmed(rate, 2)} fps`

export const formatBitRate = bitsPerSecond => {
  if (bitsPerSecond >= 1_000_000) return `${(bitsPerSecond / 1_000_000).toFixed(1)} Mb/s`

  return `${Math.round(bitsPerSecond / 1000)} kb/s`
}

export const formatSampleRate = hertz => `${trimmed(hertz / 1000, 1)} kHz`

const CHANNELS = {'1': 'Mono', '2': 'Stereo', '6': '5.1', '8': '7.1'}

export const formatChannels = count => CHANNELS[count] || `${count} channels`

export const formatDimensions = (width, height) => `${width} × ${height}`

export const formatMegapixels = (width, height) => `${trimmed((width * height) / 1_000_000, 1)} MP`

// The names a resolution goes by, keyed by its shorter side.
const RESOLUTIONS = [[2160, '4K'], [1440, '1440p'], [1080, '1080p'], [720, '720p'], [480, '480p']]

export const formatResolution = (width, height) => {
  const shorter = Math.min(width, height)
  const [, name] = RESOLUTIONS.find(([lines]) => shorter >= lines) || []

  return name || formatDimensions(width, height)
}

const hemisphere = (degrees, positive, negative) => `${Math.abs(degrees).toFixed(4)}° ${degrees < 0 ? negative : positive}`

export const formatCoordinates = (latitude, longitude) =>
  `${hemisphere(latitude, 'N', 'S')}, ${hemisphere(longitude, 'E', 'W')}`

export const formatAltitude = metres => `${Math.round(metres)} m`

// A maker that already opens the model's name is not said twice: "Canon EOS R5", not "Canon Canon EOS R5".
export const deviceName = ({make, model}) => {
  if (!model) return make || null
  if (!make || model.toLowerCase().startsWith(make.toLowerCase())) return model

  return `${make} ${model}`
}

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})?$/

const zoneName = offset => {
  const [hours, minutes] = offset.slice(1).split(':').map(Number)
  const sign = offset[0] === '-' ? '−' : '+'

  return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`
}

/**
 * When it was taken, as "May 18, 2026 · 4:12 PM", with the zone it was taken in when the file says.
 * "null" for a moment that doesn't read as one.
 */
export const formatTakenAt = takenAt => {
  const parts = WALL_CLOCK.exec(takenAt || '')

  if (!parts) return null

  const [, year, month, day, hour, minute, second, zone] = parts
  const dateOptions = {month: 'short', day: 'numeric', year: 'numeric'}
  const timeOptions = {hour: 'numeric', minute: '2-digit'}

  if (zone === 'Z') {
    const moment = new Date(takenAt)

    return `${moment.toLocaleDateString('en-US', dateOptions)} · ${moment.toLocaleTimeString('en-US', timeOptions)}`
  }

  // Built in UTC and read back in UTC, so the wall clock comes out as it was, whatever zone the viewer is in.
  const wall = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  const date = wall.toLocaleDateString('en-US', {...dateOptions, timeZone: 'UTC'})
  const time = wall.toLocaleTimeString('en-US', {...timeOptions, timeZone: 'UTC'})

  return `${date} · ${time}${zone ? ` · ${zoneName(zone)}` : ''}`
}

const row = (label, value, mono = false) => ({label, value, mono})

const missing = value => (value ?? null) === null

const present = rows => rows.filter(({value}) => !missing(value) && value !== '')

const when = (value, format) => (missing(value) ? null : format(value))

const sizeOf = ({width, height}) => (width && height ? formatDimensions(width, height) : null)

const TAKEN = {image: 'Taken', video: 'Recorded', audio: 'Recorded'}

const photoLead = details => present([
  row('Aperture', when(details.aperture, formatAperture)),
  row('Shutter', when(details.exposureTime, formatShutter)),
  row('ISO', when(details.iso, iso => `ISO ${iso}`)),
  row('Focal length', when(details.focalLength35mm ?? details.focalLength, formatFocalLength))
])

const videoLead = (details, runtime) => present([
  row('Resolution', details.width && details.height ? formatResolution(details.width, details.height) : null),
  row('Frame rate', when(details.frameRate, formatFrameRate)),
  row('Runtime', runtime),
  row('Codec', details.videoCodec)
])

const recordingLead = (details, runtime) => present([
  row('Runtime', runtime),
  row('Sample rate', when(details.sampleRate, formatSampleRate)),
  row('Bit rate', when(details.bitRate, formatBitRate)),
  row('Channels', when(details.channels, formatChannels))
])

// The lens's own focal length, with what it equals on 35mm film when that is different.
const focalLengths = ({focalLength, focalLength35mm}) => {
  if (!focalLength) return null
  if (!focalLength35mm || Math.round(focalLength35mm) === Math.round(focalLength)) return formatFocalLength(focalLength)

  return `${formatFocalLength(focalLength)} (${formatFocalLength(focalLength35mm)} on 35mm)`
}

const photoGroups = (details, file) => [
  {
    title: details.make || details.model ? 'Taken with' : 'Made with',
    rows: present([
      row('Device', deviceName(details)),
      row('Lens', details.lens),
      row('Focal length', focalLengths(details)),
      row('Exposure bias', when(details.exposureBias, formatExposureBias), true),
      row('Flash', details.flash),
      row('White balance', details.whiteBalance),
      row('Software', details.software)
    ])
  },
  {
    title: 'Picture',
    rows: present([
      row('Dimensions', sizeOf(details), true),
      row('Megapixels', details.width && details.height ? formatMegapixels(details.width, details.height) : null),
      row('Colour', details.colourProfile),
      row('Format', fileExtension(file.filename).toUpperCase())
    ])
  }
]

const videoGroups = (details, file) => [
  {title: 'Recorded with', rows: present([row('Device', deviceName(details)), row('Software', details.software)])},
  {
    title: 'Picture',
    rows: present([
      row('Dimensions', sizeOf(details), true),
      row('Codec', details.videoCodec),
      row('Bit rate', when(details.bitRate, formatBitRate), true),
      row('Format', fileExtension(file.filename).toUpperCase())
    ])
  },
  {
    title: 'Sound',
    rows: present([
      row('Codec', details.audioCodec),
      row('Sample rate', when(details.sampleRate, formatSampleRate)),
      row('Channels', when(details.channels, formatChannels))
    ])
  }
]

const recordingGroups = (details, file) => [
  {title: 'Recorded with', rows: present([row('Device', deviceName(details)), row('Software', details.software), row('Title', details.title)])},
  {
    title: 'Sound',
    rows: present([row('Codec', details.audioCodec), row('Format', fileExtension(file.filename).toUpperCase())])
  }
]

/**
 * What the details sheet shows for a file: the "lead" its kind is read by, when and where it was
 * taken, and the groups of everything else, each without the rows the file had nothing for. With
 * no details at all there is still the file's place in the drive to show. "runtime" is written out
 * by the player's own clock, so the sheet and the transport agree.
 */
export const describe = (file, kind, details, runtime = null) => {
  const known = details || {}
  const lead = {image: photoLead, video: videoLead, audio: recordingLead}[kind]?.(known, runtime) || []
  const groups = {image: photoGroups, video: videoGroups, audio: recordingGroups}[kind]?.(known, file) || []
  const takenAt = formatTakenAt(known.takenAt)
  const place = {
    name: known.place,
    latitude: known.latitude,
    longitude: known.longitude,
    coordinates: missing(known.latitude) || missing(known.longitude) ? null : formatCoordinates(known.latitude, known.longitude),
    altitude: when(known.altitude, formatAltitude)
  }

  return {
    lead,
    taken: takenAt && {label: TAKEN[kind] || 'Made', at: takenAt},
    place: place.coordinates && place,
    groups: [
      ...groups,
      {
        title: 'In this drive',
        rows: present([row('Size', formatBytes(file.filesize), true), row('Added', formatTimestamp(file.updatedAt))])
      }
    ].filter(({rows}) => rows.length)
  }
}
